import React, { memo, useState, useRef, useEffect } from 'react';

interface MagnifierProps {
  imgSrc: string;
  imgWidth?: number;
  imgHeight?: number;
  magnifierRadius: number;
}

function Magnifier({
  imgSrc,
  imgHeight,
  imgWidth,
  magnifierRadius
}: MagnifierProps) {
  // Store the position of the magnifier and position of the large image relative to the magnifier.
  const [magnifierState, setMagnifierState] = useState({
    top: 0,
    left: 0,
    offsetX: 0,
    offsetY: 0
  });

  // Store whether the magnifier is currently visible.
  const [isVisible, setIsVisible] = useState(false);
  const wrapRef = useRef(null);
  const imgRef = useRef(null);

  // Shared calc for both mouse and touch positions.
  const updateMagnifier = (clientX, clientY) => {
    const smallImage = imgRef.current;
    if (!smallImage) return;
    const rect = smallImage.getBoundingClientRect();
    let x = clientX - rect.left;
    let y = clientY - rect.top;
    // Clamp within image bounds so dragging near edges doesn't fly off.
    x = Math.max(0, Math.min(x, rect.width));
    y = Math.max(0, Math.min(y, rect.height));
    setMagnifierState({
      top: y - magnifierRadius,
      left: x - magnifierRadius,
      offsetX: (x / rect.width) * smallImage.naturalWidth - magnifierRadius,
      offsetY: (y / rect.height) * smallImage.naturalHeight - magnifierRadius
    });
  };

  const handleTouchStart = (e) => {
    setIsVisible(true);
    const touch = e.touches[0];
    updateMagnifier(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    // Prevent page scroll while dragging the lens.
    e.preventDefault();
    const touch = e.touches[0];
    updateMagnifier(touch.clientX, touch.clientY);
  };

  // Dismiss the lens on any tap/click outside the image wrapper (mobile).
  useEffect(() => {
    if (!isVisible) return;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsVisible(false);
      }
    };
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("mousedown", handleOutside);
    return () => {
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [isVisible]);

  return (
      <div ref={wrapRef} style={{ position: "relative" }}>
        <img
          ref={imgRef}
          src={imgSrc}
          // Set the intrinsic width of the element (optional).
          width={imgWidth}
          height={imgHeight}
          alt="zoom effects"
          // Image can be a maximum of 50% of the viewport in either direction.
          style={{
            maxHeight: "70vh",
            maxWidth: "100%",
            height: "auto",
            width: "100%",
            touchAction: "none"
          }}
          // Desktop: set the magnifier state on every move of the mouse over the image.
          onMouseMove={(e) => {
            setIsVisible(true);
            updateMagnifier(e.clientX, e.clientY);
          }}
          // Hide the magnifier when leaving the image (desktop).
          onMouseLeave={() => setIsVisible(false)}
          // Mobile: touch and drag to move the lens.
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        />
        <div
          style={{
            // Constants:
            boxShadow: "0 5px 10px -2px rgba(0, 0, 0, 0.3)",
            pointerEvents: "none",
            position: "absolute",
            border: "4px solid #efefef",
            zIndex: 99,
            display: "block",
            transition: "opacity 0.2s",
            // Set background to the image from props:
            background: `url("${imgSrc}") no-repeat #fff`,
            // Set sizing based on the magnifierRadius from props:
            width: 2 * magnifierRadius,
            height: 2 * magnifierRadius,
            borderRadius: magnifierRadius,
            // Set position based on on the magnifier state:
            top: magnifierState.top + "px",
            left: magnifierState.left + "px",
            backgroundPositionX: -1 * magnifierState.offsetX,
            backgroundPositionY: -1 * magnifierState.offsetY,
            // Toggle opacity based on the isVisible state:
            opacity: isVisible ? 1 : 0
          }}
        />
      </div>
  );
}

export default memo(Magnifier)